import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearReceiptsCSVReportV2,
  FETCH_RECEIPTS_CSV_REPORT_V2,
  fetchReceiptsCSVReportV2,
} from '~/actions';
import { shouldUseDsoStructure } from '~/growthbook';
import { useLoading, useParentOffice } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useUserSelector } from '~/hooks/useUserSelector';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';
import { ReceiptTransactionDateEnum } from '~/utils/Enum';
import { useScopeReceiptOfficeIds } from '../../../hooks/useScopeReceiptOfficeIds';

/** @param scope - the offices the report covers, as the Payment History table it sits over */
export const useDownloadReportV2 = (scope: TViewedOfficeScope) => {
  const dispatch = useDispatch();
  const user = useUserSelector();

  const isDSOStructureEnabled = useRef(shouldUseDsoStructure()).current;

  const offices = useAppSelector((state) => state.user.offices?.map((office) => office.id));
  const childOffices = useAppSelector((state) =>
    state.user.childOffices?.map((office) => office.id),
  );
  const reports = useAppSelector((state) => state.receipt.reports);

  const { isLoading } = useLoading([FETCH_RECEIPTS_CSV_REPORT_V2]);
  const { isParentOffice } = useParentOffice();

  const scopeOfficeIds = useScopeReceiptOfficeIds(scope);

  const officeIds = useMemo(() => {
    if (scopeOfficeIds) {
      return scopeOfficeIds;
    }

    if (!isDSOStructureEnabled && !isParentOffice) {
      return [user?.id];
    }

    if (!isDSOStructureEnabled) {
      return [user?.id, ...childOffices];
    }

    return offices;
  }, [childOffices, isDSOStructureEnabled, isParentOffice, offices, scopeOfficeIds, user?.id]);

  const onDownloadReport = (formDateRange: ReceiptTransactionDateEnum) => {
    dispatch(
      fetchReceiptsCSVReportV2({
        officeIds,
        transactionDate: formDateRange,
        scope,
      }),
    );
  };

  useEffect(() => {
    if (typeof reports === 'string' && reports.trim() !== '') {
      const blob = new Blob([reports], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'receiptReport.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => dispatch(clearReceiptsCSVReportV2()), 350);
    }
  }, [dispatch, reports]);

  return {
    isLoading,
    reports,
    onDownloadReport,
  };
};
